import { supabase } from "@/integrations/supabase/client";
import { FunctionsHttpError } from "@supabase/supabase-js";

/**
 * Billing helpers.
 *
 * An existing subscriber must NEVER be sent through create-checkout-session —
 * that opens a SECOND Stripe subscription (double charge + duplicate company).
 * The server returns 409 { error: 'existing_subscription' } in that case, and
 * the client routes to change-subscription-plan instead, behind a confirm
 * dialog that states the prorated charge.
 */

export type CheckoutBody = {
  email: string;
  seats: number;
  userId: string;
  userType: string;
  fmcsaData?: { legalName?: string | null; dotNumber?: string | null; mcNumber?: string | null };
  successUrl?: string;
  cancelUrl?: string;
};

export type CheckoutOutcome =
  | { kind: "redirect"; url: string }
  | { kind: "existing_subscription"; message: string }
  | { kind: "error"; message: string };

async function readFunctionError(error: unknown): Promise<{ code?: string; message?: string; status?: number }> {
  if (error instanceof FunctionsHttpError) {
    const status = error.context?.status as number | undefined;
    try {
      const body = await error.context.json();
      return { code: body?.error, message: body?.message ?? body?.error, status };
    } catch {
      return { status, message: error.message };
    }
  }
  return { message: (error as Error)?.message };
}

/** Starts a first-time checkout, or reports that a subscription already exists. */
export async function startCheckoutOrDetectExisting(body: CheckoutBody): Promise<CheckoutOutcome> {
  const { data, error } = await supabase.functions.invoke("create-checkout-session", { body });

  if (error) {
    const parsed = await readFunctionError(error);
    if (parsed.code === "existing_subscription" || parsed.status === 409) {
      return {
        kind: "existing_subscription",
        message: parsed.message ?? "You already have an active subscription.",
      };
    }
    return { kind: "error", message: parsed.message ?? "Could not start checkout." };
  }

  if (data?.error === "existing_subscription") {
    return { kind: "existing_subscription", message: data?.message ?? "You already have an active subscription." };
  }
  if (data?.url) return { kind: "redirect", url: data.url };
  return { kind: "error", message: "No checkout URL returned." };
}

export type ChangePlanResult =
  | { ok: true; newTier: string; newBillingMode: string | null }
  | { ok: false; message: string };

/** Switches the caller's EXISTING Stripe subscription to another tier (prorated). */
export async function changeSubscriptionPlan(userType: string, seats?: number): Promise<ChangePlanResult> {
  const { data, error } = await supabase.functions.invoke("change-subscription-plan", {
    body: { userType, ...(seats ? { seats } : {}) },
  });

  if (error) {
    const parsed = await readFunctionError(error);
    return { ok: false, message: parsed.message ?? "Could not change your plan." };
  }
  if (data?.ok) {
    return { ok: true, newTier: data.newTier, newBillingMode: data.newBillingMode ?? null };
  }
  return { ok: false, message: data?.message ?? data?.error ?? "Could not change your plan." };
}
